/* eslint-disable react/no-danger */
import React, { useState, useRef, useEffect, type ReactNode, useCallback } from 'react'
import { cx } from '@/utils/strings'
import { BwTableTooltip, BwTableTooltipTrigger, BwTableTooltipContent } from '@/components/ui/tooltip'
import Collapsible from '@/components/ui/collapsible'
import useStickyHelper from '@/utils/useStickyHelper'
import HeadingWithAnchor from '@/components/heading-with-anchor'
import SafeHTML from './safe-html'
import DynamicAction, { type DynamicActionProps } from '@/components/ui/dynamic-action'

/**
 * The gap between the header and the stickied element.
 */
const STICKIED_GAP = 10

type ComparisonGridProps = {
  data: {
    content: TableData | string
  }
  textAlign?: 'Left' | 'Center'
  layout?: 'Default' | 'First Column Wide' | 'Second Column Wide'
  isPrintView?: boolean
}

export type TableRowData = {
  subheader?: string
  Tooltip?: ReactNode
  cells: (
    | ReactNode
    | {
        linkUrl: string
        linkText: string
        text: string
        icon: string
        subtext: string
      }
  )[]
}

export type TableData = {
  id?: string
  tableEyebrowText?: string
  tableTitle: string
  anchorLinkTitle?: string
  subtext?: React.ReactNode
  actions?: DynamicActionProps[]
  headers?: (ReactNode | Record<string, ReactNode>)[]
  body: TableRowData[]
  footer?: TableRowData[]
}

function ExpandIcon({ className = '', isExpanded = false, openIcon = '&#xe921;', closeIcon = '&#xe925;' }) {
  const iconStyle = 'absolute left-0 top-0 transition-[transform,opacity] w-full h-full leading-[100%] text-primaryBlue'
  return (
    <span className={`${className} relative inline-block font-bwi text-indigoBlue`}>
      <span
        className={cx(iconStyle, isExpanded ? `opacity-100` : `opacity-0`)}
        dangerouslySetInnerHTML={{ __html: closeIcon }}
      />
      <span
        className={cx(iconStyle, !isExpanded ? `opacity-100` : `opacity-0`)}
        dangerouslySetInnerHTML={{ __html: openIcon }}
      />
    </span>
  )
}

export default function ComparisonGrid({
  data,
  textAlign = 'Center',
  layout = 'Default',
  isPrintView,
}: ComparisonGridProps) {
  const tableData: TableData | undefined = typeof data.content === 'object' ? data.content : JSON.parse(data.content)

  const [screenWidth, setScreenWidth] = useState(0)

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth)
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const containerRef = useRef<HTMLDivElement>(null)
  const { stickyTracker, top, isStuck, floatingOffset } = useStickyHelper({ containerRef })

  const columns = tableData?.headers?.length ?? 0
  const hasFooter = (tableData?.footer?.length ?? 0) > 0

  const lastRowStyles = (column: number) => [
    'z-1',
    column === 0 ? 'col-span-full md:col-auto' : 'md:border-l md:border-r',
  ]

  const headerRef = useRef<HTMLDivElement>(null)
  const headerHeight = headerRef.current?.offsetHeight || 0
  const textAlignStyle =
    !textAlign || textAlign === 'Center' ? `text-center text-balance` : `text-center text-balance md:text-left`

  const [expanded, setExpanded] = React.useState<Record<number, boolean>>({
    // Expand the first 3 rows by default
    0: true,
    1: true,
    2: true,
  })
  const isSomeExpanded = Object.keys(expanded).some((key) => expanded[Number(key)])
  const toggleAllRows = () => {
    if (isSomeExpanded) {
      // Collapse all
      setExpanded({})
    } else {
      // Expand all
      setExpanded(tableData?.body.reduce((acc, _, index) => ({ ...acc, [index]: true }), {}) as Record<number, boolean>)
    }
  }

  const getColsStyle = (columns: number) => {
    if (layout === 'First Column Wide') {
      switch (columns) {
        case 1:
          return 'grid-cols-1'
        case 2:
          return 'grid-cols-1 md:grid-cols-[3fr_minmax(auto,1fr)]'
        case 3:
          return 'grid-cols-2 md:grid-cols-[3fr_repeat(2,minmax(auto,1fr))]'
        case 4:
          return 'grid-cols-3 md:grid-cols-[3fr_repeat(3,minmax(auto,1fr))]'
        case 5:
          return 'grid-cols-4 md:grid-cols-[3fr_repeat(4,minmax(auto,1fr))]'
        case 6:
          return 'grid-cols-5 md:grid-cols-[3fr_repeat(5,minmax(auto,1fr))] lg:grid-cols-[2fr_repeat(5,minmax(auto,1fr))]'
        case 7:
          return 'grid-cols-6 md:grid-cols-[3fr_repeat(6,minmax(auto,1fr))] lg:grid-cols-[2fr_repeat(6,minmax(auto,1fr))]'
        case 8:
          return 'grid-cols-7 md:grid-cols-[3fr_repeat(7,minmax(auto,1fr))] lg:grid-cols-[2fr_repeat(7,minmax(auto,1fr))]'
      }
    }
    if (layout === 'Second Column Wide') {
      switch (columns) {
        case 1:
          return 'grid-cols-1'
        case 2:
          return 'grid-cols-1 md:grid-cols-[2fr_2fr]'
        case 3:
          return 'grid-cols-2 md:grid-cols-[2fr_2fr_minmax(auto,1fr)]'
        case 4:
          return 'grid-cols-3 md:grid-cols-[2fr_2fr_repeat(2,minmax(auto,1fr))]'
        case 5:
          return 'grid-cols-4 md:grid-cols-[2fr_2fr_repeat(3,minmax(auto,1fr))]'
        case 6:
          return 'grid-cols-5 md:grid-cols-[2fr_3fr_repeat(4,minmax(auto,1fr))] lg:grid-cols-[2fr_2fr_repeat(4,minmax(auto,1fr))]'
        case 7:
          return 'grid-cols-6 md:grid-cols-[2fr_3fr_repeat(5,minmax(auto,1fr))] lg:grid-cols-[2fr_2fr_repeat(5,minmax(auto,1fr))]'
        case 8:
          return 'grid-cols-7 md:grid-cols-[2fr_3fr_repeat(6,minmax(auto,1fr))] lg:grid-cols-[2fr_2fr_repeat(6,minmax(auto,1fr))]'
      }
    }

    switch (columns) {
      case 1:
        return 'grid-cols-1'
      case 2:
        return 'grid-cols-1 md:grid-cols-2'
      case 3:
        return 'grid-cols-2 md:grid-cols-3'
      case 4:
        return 'grid-cols-3 md:grid-cols-4'
      case 5:
        return 'grid-cols-4 md:grid-cols-5'
      case 6:
        return 'grid-cols-5 md:grid-cols-6'
      case 7:
        return 'grid-cols-6 md:grid-cols-7'
      case 8:
        return 'grid-cols-7 md:grid-cols-8'
    }
  }

  const BodyRowWrapper = useCallback(
    ({ children }: { children: ReactNode }) => (
      <>{screenWidth > 768 ? <div className="contents group">{children}</div> : children}</>
    ),
    [screenWidth]
  )

  const BodyRowInner = useCallback(
    ({ children }: { children: ReactNode }) => (
      <>
        {screenWidth > 768 ? (
          <div className="group-hover:bg-secondary-100 transition-colors duration-200">{children}</div>
        ) : (
          children
        )}
      </>
    ),
    [screenWidth]
  )

  return (
    <section
      id={tableData?.id ?? undefined}
      ref={containerRef}
      className={cx(
        'comparison-grid',
        'bg-secondary-50 dark:bg-transparent relative [&_.rich-icon]:ml-1 [&_a]:no-underline [&_p:last-child]:mb-0!',
        isPrintView && 'bg-white'
      )}
    >
      {/* Mask for hiding grid above sticky header */}
      <div
        className="sticky left-0 z-2 w-full pointer-events-none"
        style={{
          opacity: isStuck ? 1 : 0,
          top: `${top + headerHeight}px`,
        }}
      >
        <div
          className="absolute top-0 left-0 z-10 w-full from-secondary-50 dark:from-secondary-900 to-transparent bg-linear-to-b from-100%"
          style={{
            height: `${top + headerHeight + STICKIED_GAP - floatingOffset}px`,
            top: `-${top + headerHeight}px`,
          }}
        />
      </div>
      <div className={cx('mx-auto', !isPrintView && 'px-5 md:px-9 max-w-8xl', isPrintView && 'px-0 max-w-none')}>
        <div className="text-center grid gap-4 mb-8">
          {tableData?.tableEyebrowText && <span className="text-eyebrow uppercase">{tableData.tableEyebrowText}</span>}
          {tableData?.tableTitle && (
            <div className="relative z-3 mb-0">
              {tableData?.anchorLinkTitle ? (
                <HeadingWithAnchor
                  className="relative inline-block"
                  type="h3"
                  id={tableData?.anchorLinkTitle}
                  title={tableData?.tableTitle}
                />
              ) : (
                <h3>{tableData?.tableTitle}</h3>
              )}
            </div>
          )}

          {tableData?.subtext && <div className="relative z-3 [&_p]:mb-0 max-w-lg m-auto">{tableData?.subtext}</div>}
          {tableData?.actions && (
            <div>
              {tableData?.actions.map((action, actionIndex) => (
                <DynamicAction
                  key={action.sys?.id ? `action-${action.sys.id}` : `action-${actionIndex}`}
                  {...action}
                  className="mr-3 mb-3"
                />
              ))}
            </div>
          )}
        </div>
        <div className={cx('grid relative -mx-5 sm:mx-0', getColsStyle(columns), isPrintView && 'border')}>
          {stickyTracker}

          {/* Headers */}
          {tableData?.headers?.map((header, indexColumn) => {
            const col = `column${indexColumn + 1}`
            // Try to use header content for uniqueness if possible
            let headerKey = `header-${col}-${indexColumn}`
            if (typeof header === 'object' && (header as any)?.key) {
              headerKey = `header-${col}-${(header as any).key}`
            }
            return (
              <div
                key={headerKey}
                ref={indexColumn === 0 ? headerRef : undefined}
                className={cx(
                  'font-heading flex justify-between content-center',
                  'py-3 px-2',
                  'md:px-3 md:mx-3 md:py-4',
                  '[p]:mb-0 [&_p]:text-2xl! [&_h6]:mb-3! [&_h6]:text-base!',
                  'md:[&_p]:mb-3! md:sticky md:z-10 md:text-2xl!',
                  indexColumn === 0 && 'md:ml-0 md:mr-3 md:p-5! dark:bg-secondary-900',
                  indexColumn === 0
                    ? `sticky z-10 text-left col-span-full md:col-auto lg:px-2 lg:text-lg bg-secondary-50 border-b`
                    : `dark:bg-secondary-800 border-t border-r border-l bg-white rounded-t-lg hidden md:block ${textAlignStyle}`,
                  isStuck && indexColumn === 0 && `border-b-0`,
                  isPrintView && 'pt-3! pb-3! *:pb-0! shadow-none! border-b-[1px]!',
                  indexColumn === columns - 1 && 'md:mr-0'
                )}
                style={{
                  top: `${top + STICKIED_GAP - floatingOffset}px`,
                }}
              >
                <div className={cx('flex items-center flex-col', textAlign === 'Center' && 'justify-center')}>
                  {header?.hasOwnProperty?.(col) ? (header as any)[col] : header}
                </div>
                {indexColumn === 0 && (
                  <button
                    type="button"
                    className="md:hidden box-content"
                    onClick={toggleAllRows}
                    aria-label={isSomeExpanded ? 'Collapse all' : 'Expand all'}
                  >
                    <ExpandIcon className="text-[22px] h-6 w-6" isExpanded={isSomeExpanded} />
                  </button>
                )}
              </div>
            )
          })}
          <div
            className={cx('col-span-full sticky', isStuck ? `shadow-md drop-shadow-secondary-800/40 block` : `hidden`)}
            style={{
              height: `${STICKIED_GAP}px`,
              top: `${top + headerHeight - floatingOffset}px`,
            }}
          />
          {/* Body */}
          {tableData?.body.map((row, indexRow, rows) =>
            row.subheader ? (
              <div
                key={`subheader-${indexRow}-${row.subheader}`}
                className="px-2 py-5 col-span-full border-b border-t dark:border-secondary-500 z-1 md:px-5"
              >
                <span className="text-heading text-2xl">{row.subheader}</span>
              </div>
            ) : (
              <BodyRowWrapper key={`bodyrow-${indexRow}-${row.subheader || ''}`}>
                {tableData?.headers?.map((header, indexColumn) => {
                  const col = `column${indexColumn + 1}`
                  const isLastRow = rows.length - 1 - indexRow === 0
                  const cell = row?.cells?.[indexColumn] || (row as any)?.[col]
                  const isExpanded = expanded[indexRow]
                  // First header cell content
                  const firstColumnContent = (
                    <div className="flex items-center content-between gap-2">
                      {/* Feature heading */}
                      <div className="flex-1">
                        {cell?.linkUrl ? (
                          <>
                            <a className="flex items-center" href={cell.linkUrl}>
                              {cell.text}
                              {cell.icon === 'iconLink' && (
                                <span className="font-bwi ml-1 text-[18px] text-primaryBlue">&#xe91b;</span>
                              )}
                            </a>
                            {cell.subtext && <span className="text-xs">{cell.subtext}</span>}
                          </>
                        ) : (
                          <span
                            className={cx(
                              '[&_p]:last:mb-0 [&_p]:text-base [&_.rich-icon]:hidden md:[&_.rich-icon]:inline-block [&_.rich-icon]:text-[14px]! [&_p]:font-semibold! [&_a]:py-2'
                            )}
                          >
                            {cell}
                          </span>
                        )}
                      </div>
                      <ExpandIcon className="md:hidden w-4 h-4 text-base" isExpanded={isExpanded} />
                    </div>
                  )

                  // Body cell content
                  let bodyCellContent
                  if (cell?.linkText) {
                    bodyCellContent = (
                      <>
                        <a className="text-primaryBlue" href={cell.linkUrl}>
                          {cell.linkText}
                        </a>
                        <br />
                        {cell.text}
                      </>
                    )
                  } else if (cell === 'iconTick') {
                    bodyCellContent = <span className="font-bwi text-[20px]">&#xe912;</span>
                  } else if (typeof cell === 'string') {
                    bodyCellContent = <SafeHTML html={cell} />
                  } else {
                    bodyCellContent = cell
                  }

                  // Final content based on column index
                  const content = indexColumn === 0 ? firstColumnContent : bodyCellContent

                  return (
                    <BodyRowInner key={`bodycell-${indexRow}-${indexColumn}-${col}`}>
                      <div
                        className={cx(
                          'z-1 md:mx-3 h-full',
                          indexColumn === 0
                            ? 'col-span-full md:col-auto md:mr-3 border-b md:border-b-0'
                            : 'md:border-l md:border-r',
                          indexColumn === columns - 1 && 'border-r-0',
                          indexColumn === columns - 1 && 'md:mr-0'
                        )}
                      >
                        {col === 'column1' ? (
                          <>
                            {/* Desktop */}
                            <div className="relative [&_button]:w-full  hidden md:block mx-2">
                              {row.Tooltip && !isPrintView ? (
                                <BwTableTooltip>
                                  <BwTableTooltipTrigger>{content}</BwTableTooltipTrigger>
                                  <BwTableTooltipContent className={`[&_p:last-child]:mb-0 [&_p]:text-sm`}>
                                    {row.Tooltip}
                                  </BwTableTooltipContent>
                                </BwTableTooltip>
                              ) : (
                                <div className="text-[13px] py-3">{content}</div>
                              )}
                              {/* Sometimes the tooltip button is absolute and ignores the height of the content */}
                              {/* This div is a hidden placeholder to fill the height of the cell */}
                              {/* <div
                                className={cx(
                                  'hidden leading-tight text-base h-full  md:flex lg:text-lg lg:px-14',
                                  !!row.Tooltip && 'opacity-0 z-0',
                                  isPrintView && 'block opacity-100 py-3'
                                )}
                              >
                                {content}
                              </div> */}
                            </div>
                            {/* Mobile */}
                            <button
                              type="button"
                              className={cx(
                                'w-full pl-2 pr-3 md:px-3 py-4 text-[14px] sm:p-4 sm:text-base text-left md:hidden',
                                !isExpanded && indexColumn > 0 && 'hidden'
                              )}
                              onClick={(e) => {
                                const target = e.target as HTMLElement
                                if (target && !isExpanded) {
                                  setTimeout(() => {
                                    target.scrollIntoView({ behavior: 'smooth', block: 'center' })
                                  }, 100)
                                }
                                setExpanded({ ...expanded, [indexRow]: !isExpanded })
                              }}
                              aria-label={isExpanded ? 'Collapse' : 'Expand'}
                            >
                              <div className="font-semibold">{content}</div>
                            </button>
                            {row.Tooltip && (
                              <Collapsible className="md:h-auto! md:opacity-100! md:visible!" isOpen={isExpanded}>
                                <div className={cx('md:hidden text-sm text-secondary-500 pt-1.5 px-2 sm:px-4 pb-4')}>
                                  {row.Tooltip}
                                </div>
                              </Collapsible>
                            )}
                          </>
                        ) : (
                          <Collapsible className="md:h-auto! md:opacity-100! md:visible!" isOpen={isExpanded}>
                            <div
                              className={cx(
                                `text-[13px] p-3 sm:[&_p]:text-base [&_.rich-icon]:text-[20px]! sm:[&_.rich-icon]:text-[20px]! `,
                                textAlignStyle
                              )}
                            >
                              <div className="[&_a]:hidden [&_h6]:hidden [&_strong]:font-medium font-medium sm:[&_strong]:font-semibiold! sm:font-semibold md:hidden">
                                {header?.hasOwnProperty?.(col) ? (header as any)[col] : header}
                              </div>
                              {content}
                            </div>
                          </Collapsible>
                        )}
                      </div>
                    </BodyRowInner>
                  )
                })}
              </BodyRowWrapper>
            )
          )}
          {/* Footer */}
          {tableData?.footer?.map((row, index) =>
            row.subheader ? (
              <div
                key={`footer-subheader-${index}-${row.subheader}`}
                className="col-span-full px-4 py-7 md:px-12 border-b dark:border-secondary-500 font-bold z-1"
              >
                {row.subheader}
              </div>
            ) : (
              <BodyRowWrapper key={`footer-row-${index}`}>
                {tableData?.headers?.map((header, indexColumn) => {
                  const col = `column${indexColumn + 1}`
                  const cell = row?.cells?.[indexColumn] || (row as any)?.[col]
                  const isLastColumn = indexColumn === columns - 1

                  return (
                    <BodyRowInner key={`footercell-${index}-${indexColumn}`}>
                      <div
                        className={cx(
                          ...lastRowStyles(indexColumn),
                          'pt-4 pb-11 px-2 font-bold lg:text-lg ml-3 h-full',
                          indexColumn === 0 && 'pb-4 md:pb-11 mr-3',
                          !isLastColumn && 'mr-3'
                        )}
                      >
                        {col === 'column1' ? (
                          cell
                        ) : (
                          <div className={textAlignStyle}>
                            <div className="pb-2 font-semibold md:hidden">
                              {header?.hasOwnProperty?.(col) ? (header as any)[col] : header}
                            </div>
                            {cell?.text ? (
                              <>
                                <SafeHTML html={cell.text} />
                                <br />
                                {cell.linkText && (
                                  <a className="text-primaryBlue" href={cell.linkUrl}>
                                    {cell.linkText}
                                  </a>
                                )}
                              </>
                            ) : (
                              cell
                            )}
                          </div>
                        )}
                      </div>
                    </BodyRowInner>
                  )
                })}
              </BodyRowWrapper>
            )
          )}
        </div>
      </div>
    </section>
  )
}
